"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateAngleAndScale = exports.fromImageProjection = exports.toImageProjection = exports.ImageLayer = void 0;
const Collection_1 = __importDefault(require("ol/Collection"));
const Feature_1 = __importDefault(require("ol/Feature"));
const Point_1 = __importDefault(require("ol/geom/Point"));
const Translate_1 = __importDefault(require("ol/interaction/Translate"));
const Image_1 = __importDefault(require("ol/layer/Image"));
const Vector_1 = __importDefault(require("ol/layer/Vector"));
const proj_1 = require("ol/proj");
const Projection_1 = __importDefault(require("ol/proj/Projection"));
const ImageStatic_1 = __importDefault(require("ol/source/ImageStatic"));
const Vector_2 = __importDefault(require("ol/source/Vector"));
const Circle_1 = __importDefault(require("ol/style/Circle"));
const Fill_1 = __importDefault(require("ol/style/Fill"));
const Stroke_1 = __importDefault(require("ol/style/Stroke"));
const Style_1 = __importDefault(require("ol/style/Style"));
const react_1 = require("react");
const useMemoized_1 = require("../../hooks/useMemoized");
const useOLMap_1 = require("../../openlayers/useOLMap");
const ImageLayer = ({ opacity = 1, src, dimensions: dimensionsProp, zIndex, topLeft: topLeftProp, bottomRight: bottomRightProp, onBoundsChange, autoCenter, draggable, }) => {
    const map = useOLMap_1.useOLMap();
    const [id] = react_1.useState(() => `${Math.random()}`);
    const dimensions = useMemoized_1.useMemoized(dimensionsProp);
    const imageExtent = react_1.useMemo(() => [0, 0, dimensions[0], dimensions[1]], [dimensions]);
    const projection = react_1.useMemo(() => {
        return new Projection_1.default({
            code: `yanzi-image-${id}`,
            units: 'pixels',
            extent: imageExtent,
        });
    }, [id, imageExtent]);
    const image = react_1.useMemo(() => {
        return new ImageStatic_1.default({
            url: src,
            projection,
            imageExtent,
        });
    }, [src, projection, imageExtent]);
    const layer = react_1.useMemo(() => new Image_1.default({
        opacity,
        source: image,
        zIndex,
    }), [opacity, zIndex, image]);
    react_1.useEffect(() => {
        map.addLayer(layer);
        return () => {
            map.removeLayer(layer);
        };
    }, [map, layer]);
    const setImageBounds = useBounds(image, projection, dimensions, topLeftProp, bottomRightProp);
    useDraggableCorner(draggable, zIndex, topLeftProp, e => {
        const bounds = setImageBounds({ topLeft: e.coordinates });
        if (e.type === 'TRANSLATE_END' && onBoundsChange && draggable) {
            onBoundsChange(bounds);
        }
    });
    useDraggableCorner(draggable, zIndex, bottomRightProp, e => {
        const bounds = setImageBounds({ bottomRight: e.coordinates });
        if (e.type === 'TRANSLATE_END' && onBoundsChange && draggable) {
            onBoundsChange(bounds);
        }
    });
    useAutoCenter(autoCenter, projection);
    return null;
};
exports.ImageLayer = ImageLayer;
function useBounds(image, projection, dimensionsArray, topLeftProp, bottomRightProp) {
    const dimensions = useMemoized_1.useMemoized(dimensionsArray);
    const topLeftFromProps = useMemoized_1.useMemoized(topLeftProp);
    const bottomRightFromProps = useMemoized_1.useMemoized(bottomRightProp);
    const topLeft = react_1.useRef(topLeftFromProps);
    const bottomRight = react_1.useRef(bottomRightFromProps);
    const setImageBounds = react_1.useCallback((bounds) => {
        const tlLonLat = bounds.topLeft ? bounds.topLeft : topLeft.current;
        const brLonLat = bounds.bottomRight ? bounds.bottomRight : bottomRight.current;
        const tl = proj_1.fromLonLat(tlLonLat);
        const br = proj_1.fromLonLat(brLonLat);
        const { angle, scale } = exports.calculateAngleAndScale(tl[0], tl[1], br[0], br[1], dimensions[0], -dimensions[1]);
        proj_1.addCoordinateTransforms('EPSG:3857', projection, exports.toImageProjection(tl, dimensions, angle, scale), exports.fromImageProjection(tl, dimensions, angle, scale));
        image.refresh();
        topLeft.current = tlLonLat;
        bottomRight.current = brLonLat;
        return {
            topLeft: topLeft.current,
            bottomRight: bottomRight.current,
        };
    }, [image, dimensions, projection]);
    react_1.useEffect(() => {
        setImageBounds({ topLeft: topLeftFromProps });
    }, [topLeftFromProps, setImageBounds]);
    react_1.useEffect(() => {
        setImageBounds({ bottomRight: bottomRightFromProps });
    }, [bottomRightFromProps, setImageBounds]);
    return setImageBounds;
}
function useDraggableCorner(draggable, zIndex, positionProp, callback) {
    const map = useOLMap_1.useOLMap();
    const callbackRef = react_1.useRef(callback);
    const position = useMemoized_1.useMemoized(positionProp);
    const feature = react_1.useMemo(() => new Feature_1.default({
        geometry: new Point_1.default([0, 0]),
    }), []);
    react_1.useEffect(() => {
        ;
        feature.getGeometry().setCoordinates(proj_1.fromLonLat(position));
    }, [feature, position]);
    const source = react_1.useMemo(() => new Vector_2.default({
        features: [],
        wrapX: false,
    }), []);
    react_1.useEffect(() => {
        feature.setStyle(createStyle());
        source.addFeature(feature);
        return () => {
            source.removeFeature(feature);
        };
    }, [source, feature]);
    const layer = react_1.useMemo(() => new Vector_1.default({
        source,
        zIndex: zIndex !== undefined ? zIndex + 1 : undefined,
    }), [source, zIndex]);
    react_1.useEffect(() => {
        if (!draggable) {
            return () => { };
        }
        map.addLayer(layer);
        return () => {
            map.removeLayer(layer);
        };
    }, [map, layer, draggable]);
    const dragInteraction = react_1.useMemo(() => new Translate_1.default({ features: new Collection_1.default([feature]) }), [
        feature,
    ]);
    react_1.useEffect(() => {
        const translateEnd = () => {
            callbackRef.current({
                type: 'TRANSLATE_END',
                coordinates: proj_1.toLonLat(feature.getGeometry().getCoordinates()),
            });
        };
        dragInteraction.on('translateend', translateEnd);
        return () => {
            dragInteraction.un('translateend', translateEnd);
        };
    }, [dragInteraction, feature]);
    react_1.useEffect(() => {
        const translating = () => {
            callbackRef.current({
                type: 'TRANSLATING',
                coordinates: proj_1.toLonLat(feature.getGeometry().getCoordinates()),
            });
        };
        dragInteraction.on('translating', translating);
        return () => {
            dragInteraction.un('translating', translating);
        };
    }, [dragInteraction, feature]);
    react_1.useEffect(() => {
        if (!draggable) {
            return () => { };
        }
        map.addInteraction(dragInteraction);
        return () => {
            map.removeInteraction(dragInteraction);
        };
    }, [map, dragInteraction, draggable]);
}
function useAutoCenter(autoCenter, projection) {
    const autoCentered = react_1.useRef(false);
    const map = useOLMap_1.useOLMap();
    react_1.useEffect(() => {
        if (typeof autoCenter === 'object') {
            if ('maxZoom' in autoCenter) {
                console.warn('Using maxZoom for image layers is not supported yet.');
            }
            if ('padding' in autoCenter) {
                console.warn('Using padding for image layers is not supported yet.');
            }
        }
    }, [autoCenter]);
    react_1.useEffect(() => {
        autoCentered.current = false;
    }, [projection]);
    react_1.useEffect(() => {
        if (!autoCenter) {
            autoCentered.current = false;
        }
    }, [autoCenter]);
    react_1.useEffect(() => {
        if (!autoCenter) {
            return;
        }
        if (autoCentered.current) {
            return;
        }
        const projExtent = projection.getExtent();
        const mapProjection = 'EPSG:3857';
        const extent = proj_1.transformExtent(projExtent, projection, mapProjection);
        try {
            map.getView().fit(extent, {
                padding: [75, 75, 75, 75],
                size: map.getSize(),
            });
        }
        catch (e) {
            console.error(e);
        }
        autoCentered.current = true;
    });
}
const toImageProjection = (topLeft, dimensions, angle, scale) => ([x, y]) => {
    x -= topLeft[0];
    y -= topLeft[1];
    x /= scale;
    y /= scale;
    return [
        x * Math.cos(angle) - y * Math.sin(angle),
        y * Math.cos(angle) + x * Math.sin(angle) + dimensions[1],
    ];
};
exports.toImageProjection = toImageProjection;
const fromImageProjection = (topLeft, _dimensions, angle, scale) => ([x, y]) => {
    y *= -1;
    let xr1 = x * Math.cos(-angle) - y * Math.sin(-angle);
    let yr1 = y * Math.cos(-angle) + x * Math.sin(-angle);
    xr1 *= scale;
    yr1 *= scale;
    xr1 += topLeft[0];
    yr1 += topLeft[1];
    return [xr1, yr1];
};
exports.fromImageProjection = fromImageProjection;
const calculateAngleAndScale = (p0x, p0y, p1x, p1y, w, h) => {
    const vx = p1x - p0x;
    const vy = p1y - p0y;
    const v_norm = Math.sqrt(vx * vx + vy * vy);
    const vx_unit = vx / v_norm;
    const vy_unit = vy / v_norm;
    const dist = Math.sqrt(w * w + h * h);
    const scale = v_norm / dist;
    const angle = -Math.atan2(w * vy_unit - h * vx_unit, w * vx_unit + h * vy_unit);
    return { angle, scale };
};
exports.calculateAngleAndScale = calculateAngleAndScale;
const createStyle = () => [
    new Style_1.default({
        image: new Circle_1.default({
            stroke: new Stroke_1.default({
                width: 3,
                color: '#0a7',
            }),
            fill: new Fill_1.default({
                color: '#0da',
            }),
            radius: 9,
        }),
    }),
];
//# sourceMappingURL=index.js.map